import {
  useMutation,
  UseMutationResult,
  UseMutationOptions,
  useQuery,
  UseQueryOptions,
  UseQueryResult,
  useQueryClient,
} from '@tanstack/react-query';
import { keyBy } from 'lodash';
import { ProjectFinancialsContractType } from 'api/project';
import { ProjectAccountingAPI } from 'api/settings-financials';
import type {
  CustomRevenueRecognitionConfigApi,
  FinancialNotificationConfig,
  FinancialsConfiguration,
  RevenueRecognitionConfig,
  RevenueRecognitionConfigApi,
  UpdateFinancialAlertPayload,
} from 'api/settings-financials/types';

export const FinancialNotificationsConfigQueryKey = (): [string] => [
  'financial-notifications-key',
];

export const useFinancialNotificationsConfigQuery = (
  options: UseQueryOptions<
    FinancialNotificationConfig[],
    unknown,
    FinancialNotificationConfig[],
    [string]
  > = {},
): UseQueryResult<FinancialNotificationConfig[]> => {
  return useQuery(
    FinancialNotificationsConfigQueryKey(),
    () => ProjectAccountingAPI.getFinancialNotificationConfig(),
    {
      ...options,
    },
  );
};

export const useFinancialNotificationsConfigMutation = (
  configId: number,
  options: UseMutationOptions<
    FinancialNotificationConfig[],
    unknown,
    UpdateFinancialAlertPayload | FinancialNotificationConfig
  > = {},
): UseMutationResult<
  FinancialNotificationConfig[],
  unknown,
  UpdateFinancialAlertPayload | FinancialNotificationConfig
> => {
  const queryClient = useQueryClient();

  return useMutation(
    (payload) =>
      ProjectAccountingAPI.updateFinancialNotificationConfig(configId, payload),
    {
      onSuccess: () => {
        queryClient.refetchQueries(FinancialNotificationsConfigQueryKey());
      },
      ...options,
    },
  );
};

export const RevenueRecognitionConfigQueryKey = (): [string] => [
  'revenue-recoginition-query-key',
];

export const RevenueRecognitionProjectCountQueryKey = (): [string] => [
  'revenue-recoginition-project-count-query-key',
];

const getTransformedRevenueRecognitionConfig = (
  config: RevenueRecognitionConfigApi[],
): RevenueRecognitionConfig =>
  keyBy(config, 'financialContractType') as RevenueRecognitionConfig;

export const useRevenueRecognitionConfigQuery = (
  options: UseQueryOptions<
    RevenueRecognitionConfig,
    unknown,
    RevenueRecognitionConfig,
    [string]
  > = {},
): UseQueryResult<RevenueRecognitionConfig> => {
  return useQuery(
    RevenueRecognitionConfigQueryKey(),
    async () => {
      const config = await ProjectAccountingAPI.getRevenueRecognitionConfig();

      return getTransformedRevenueRecognitionConfig(config);
    },
    {
      ...options,
    },
  );
};

export const useRevenueRecognitionProjectCountQuery = (
  options: UseQueryOptions<
    Record<ProjectFinancialsContractType, Record<string | number, number>>,
    unknown,
    Record<ProjectFinancialsContractType, Record<string | number, number>>,
    [string]
  > = {},
): UseQueryResult<
  Record<ProjectFinancialsContractType, Record<string | number, number>>
> => {
  return useQuery(
    RevenueRecognitionProjectCountQueryKey(),
    async () => {
      const config =
        await ProjectAccountingAPI.getCustomRevenueRecognitionProjectCount();

      return config;
    },
    {
      ...options,
    },
  );
};

export const useRevenueRecognitionConfigMutation = (
  options: UseMutationOptions<
    void,
    unknown,
    RevenueRecognitionConfigApi,
    RevenueRecognitionConfig
  > = {},
): UseMutationResult<void, unknown, RevenueRecognitionConfigApi> => {
  return useMutation(
    (payload) => ProjectAccountingAPI.updateRevenueRecognitionConfig(payload),
    {
      ...options,
    },
  );
};

const CUSTOM_REVENUE_RECOGNITION_CONFIG_QUERY_KEY = [
  'custom-revenue-recognition-query',
];

export const customRevenueRecognitionQueryKey = (includeProjects = false) => [
  CUSTOM_REVENUE_RECOGNITION_CONFIG_QUERY_KEY[0],
  includeProjects.valueOf().toString(),
];

export const useCustomRevenueRecognitionQuery = (
  includeProjects = false,
  options: UseQueryOptions<
    Record<ProjectFinancialsContractType, CustomRevenueRecognitionConfigApi[]>,
    unknown,
    Record<ProjectFinancialsContractType, CustomRevenueRecognitionConfigApi[]>,
    string[]
  > = {},
) => {
  return useQuery(
    customRevenueRecognitionQueryKey(includeProjects),
    () =>
      ProjectAccountingAPI.getCustomRevenueRecognitionConfig(includeProjects),
    options,
  );
};

export const useCreateCustomRevenueRecognitionMutation = (
  options: UseMutationOptions<
    void,
    unknown,
    CustomRevenueRecognitionConfigApi,
    unknown
  > = {},
) => {
  const queryClient = useQueryClient();

  return useMutation(
    (payload) =>
      ProjectAccountingAPI.createCustomRevenueRecognitionConfig(payload),
    {
      ...options,
      onSuccess: async () => {
        await queryClient.refetchQueries(
          CUSTOM_REVENUE_RECOGNITION_CONFIG_QUERY_KEY,
        );
      },
    },
  );
};

export const useUpdateCustomRevenueRecognitionMutation = (
  customRevenueRecognitionId: number,
  options: UseMutationOptions<
    void,
    unknown,
    CustomRevenueRecognitionConfigApi,
    unknown
  > = {},
) => {
  const queryClient = useQueryClient();

  return useMutation(
    (payload) =>
      ProjectAccountingAPI.updateCustomRevenueRecognitionConfig(
        customRevenueRecognitionId,
        payload,
      ),
    {
      ...options,
      onSuccess: async () => {
        await queryClient.refetchQueries(
          CUSTOM_REVENUE_RECOGNITION_CONFIG_QUERY_KEY,
        );
      },
    },
  );
};

export const useDeleteCustomRevenueRecognitionMutation = (
  customRevenueRecognitionConfigId: number,
  options: UseMutationOptions<void, unknown, void, unknown> = {},
) => {
  const queryClient = useQueryClient();

  return useMutation(
    () =>
      ProjectAccountingAPI.deleteCustomRevenueRecognitionConfig(
        customRevenueRecognitionConfigId,
      ),
    {
      ...options,
      onSuccess: async () => {
        await queryClient.refetchQueries(
          CUSTOM_REVENUE_RECOGNITION_CONFIG_QUERY_KEY,
        );
      },
    },
  );
};

export const FinancialsConfigurationQueryKey = (): [string] => [
  'financials-configuration-query-key',
];

export const useFinancialsConfigurationQuery = (
  options: UseQueryOptions<
    FinancialsConfiguration,
    unknown,
    FinancialsConfiguration,
    [string]
  > = {},
): UseQueryResult<FinancialsConfiguration> => {
  return useQuery(
    FinancialsConfigurationQueryKey(),
    () => ProjectAccountingAPI.getFinancialsConfiguration(),
    {
      ...options,
    },
  );
};

export const useFinancialsConfigurationMutation = (
  options: UseMutationOptions<
    FinancialsConfiguration,
    unknown,
    FinancialsConfiguration
  > = {},
): UseMutationResult<
  FinancialsConfiguration,
  unknown,
  FinancialsConfiguration
> => {
  const queryClient = useQueryClient();

  return useMutation(
    (payload: FinancialsConfiguration) =>
      ProjectAccountingAPI.updateFinancialsConfiguration(payload),
    {
      onSuccess: () => {
        queryClient.refetchQueries(FinancialsConfigurationQueryKey());
      },
      ...options,
    },
  );
};
